import React from "react";
import { Card, Flex, ProviderConsumer } from "@fluentui/react-northstar";
import { Link } from "@fluentui/react";
import { EditTicketAttachmentsProps } from "./EditableTicketAttachment";
import { getFileIcon, getTruncatedFilename } from "../../EditTicketAttachments/NewAttachmentFlow/NewAttachmentFlowContent";

type AttachmentLike = EditTicketAttachmentsProps & { DownladUrl?: string; ContentUrl?: string };

const attachmentLinkStyles = {
  root: {
    fontWeight: 600,
    color: "var(--mgt-theme-brand-foreground1)",
    overflowWrap: "anywhere",
    ":hover, :active, :focus": {
      color: "var(--mgt-theme-brand-foreground1) !important",
    },
  },
} as const;

export const getAttachmentPublicUrl = (x: EditTicketAttachmentsProps) => {
  const o = x as AttachmentLike;
  return o.ContentUrl || o.DownloadUrl || o.DownladUrl || "";
};

const AttachmentLinkCard: React.FC<{
  item: EditTicketAttachmentsProps;
  globalTheme: unknown;
}> = ({ item, globalTheme }) => (
  <Card aria-roledescription="card avatar">
    <Card.Header fitted>
      <Flex gap="gap.small">
        {getFileIcon(item.FileExtension, item, globalTheme, true)}
        <Flex column styles={{ minWidth: 0 }}>
          <Link
            target="_blank"
            href={getAttachmentPublicUrl(item)}
            className="fileLink"
            styles={attachmentLinkStyles}
          >
            {getTruncatedFilename(item.FileName)}
          </Link>
        </Flex>
      </Flex>
    </Card.Header>
  </Card>
);

/** Max characters of comment body scanned (linear pass; avoids huge allocations). */
const MAX_TIKIT_IMG_SCAN_CHARS = 500_000;

/** Max `<tikit-img …>` tag matches returned (prevents pathological output size). */
const MAX_TIKIT_IMG_TAGS = 10_000;

const OPEN_TIKIT_IMG = "<tikit-img";
const CLOSE_TIKIT_IMG = "</tikit-img>";

/**
 * Extracts `itemid` from a single tag string in O(tag.length); no backtracking-heavy regex.
 */
export function extractTikitImgItemId(tag: string): string | null {
  if (tag.length > 65_536) {
    return null;
  }
  const lower = tag.toLowerCase();
  const key = "itemid";
  let pos = 0;
  while (pos < lower.length) {
    const k = lower.indexOf(key, pos);
    if (k === -1) {
      return null;
    }
    let p = k + key.length;
    while (p < lower.length && (lower[p] === " " || lower[p] === "\t" || lower[p] === "\n" || lower[p] === "\r")) {
      p++;
    }
    if (p >= lower.length || lower[p] !== "=") {
      pos = k + 1;
      continue;
    }
    p++;
    while (p < lower.length && (lower[p] === " " || lower[p] === "\t" || lower[p] === "\n" || lower[p] === "\r")) {
      p++;
    }
    if (p >= lower.length) {
      return null;
    }
    const quote = tag[p];
    if (quote !== '"' && quote !== "'") {
      pos = k + 1;
      continue;
    }
    p++;
    const start = p;
    while (p < tag.length && tag[p] !== quote) {
      p++;
    }
    if (p >= tag.length) {
      return null;
    }
    return tag.slice(start, p);
  }
  return null;
}

/**
 * Full `<tikit-img …>…</tikit-img>` tag strings. Linear in `str.length` (case-insensitive
 * markers via one lowercased copy of the scan window); avoids ReDoS from `.*?` regex.
 */
export const collectTikitImgTagStrings = (str: string): string[] => {
  const window = str.length > MAX_TIKIT_IMG_SCAN_CHARS ? str.slice(0, MAX_TIKIT_IMG_SCAN_CHARS) : str;
  const lower = window.toLowerCase();
  const matches: string[] = [];
  let i = 0;
  while (i < lower.length && matches.length < MAX_TIKIT_IMG_TAGS) {
    const openIdx = lower.indexOf(OPEN_TIKIT_IMG, i);
    if (openIdx === -1) {
      break;
    }
    const closeIdx = lower.indexOf(CLOSE_TIKIT_IMG, openIdx + OPEN_TIKIT_IMG.length);
    if (closeIdx === -1) {
      i = openIdx + OPEN_TIKIT_IMG.length;
      continue;
    }
    const tagEnd = closeIdx + CLOSE_TIKIT_IMG.length;
    const tag = window.slice(openIdx, tagEnd);
    if (extractTikitImgItemId(tag)) {
      matches.push(tag);
    }
    i = tagEnd;
  }
  return matches;
};

// Function to filter inline images from attachments (same logic as parent)
export const filterInlineImages = (str: string, attachmentsList: EditTicketAttachmentsProps[]) => {
  const matches = collectTikitImgTagStrings(str);
  let filteredAttachments = attachmentsList;
  matches.forEach(function (match) {
    const itemId = extractTikitImgItemId(match);
    if (itemId) {
      // Check both DownloadUrl and ContentUrl (if it exists) to match parent logic
      filteredAttachments = filteredAttachments.filter(x => {
        const contentUrl = getAttachmentPublicUrl(x);
        return !contentUrl?.includes(itemId);
      });
    }
  });
  return filteredAttachments.sort((a, b) => (b.IsImage ? 1 : 0) - (a.IsImage ? 1 : 0));
};

// Render attachments based on commentFileAttachments
export const renderAttachments = (
  commentBody: string,
  commentFileAttachments: EditTicketAttachmentsProps[]
): React.ReactNode => {
  if (!commentFileAttachments || commentFileAttachments.length === 0) {
    return null;
  }

  const filteredAttachments = filterInlineImages(commentBody, commentFileAttachments);
  if (filteredAttachments.length === 0) {
    return null;
  }

  let elements: React.ReactNode[] = [];
  for (let i = 0; i < filteredAttachments.length; i += 2) {
    let item1 = filteredAttachments[i];
    let item2 = filteredAttachments[i + 1];
    const el = (
      <ProviderConsumer 
        key={`attachment-${item1.Id}`}
        render={(globalTheme) => (
          <Flex className='mx-4 my-4 attachment-display' gap='gap.medium'>
            <div className='attachment-parent-group'>
              <AttachmentLinkCard item={item1} globalTheme={globalTheme} />
            </div>
            {item2 && (
              <div className='attachment-parent-group'>
                <AttachmentLinkCard item={item2} globalTheme={globalTheme} />
              </div>
            )}
          </Flex>
        )}
      />
    );
    elements.push(el);
  }
  return <>{elements}</>;
};
